// "Start a new document" templates. Each builds a small SFDT document the
// DocumentEditor opens directly (editor.open(sfdt)) — no service round-trip.
// Kept free of Syncfusion imports so the home screen stays a light chunk.

import type { HomeTemplate } from "@/features/home/homeModel";

interface Run {
    text: string;
    bold?: boolean;
    italic?: boolean;
    size?: number;
    color?: string;
}

interface Para {
    runs: Run[];
    align?: "Left" | "Center" | "Right" | "Justify";
    before?: number;
    after?: number;
    bullet?: boolean;
}

const ACCENT = "#1F4E79";
const MUTED = "#595959";

const para = (text: string | Run[], opts: Omit<Para, "runs"> = {}): Para => ({
    runs: typeof text === "string" ? [{ text }] : text,
    ...opts
});
const title = (text: string): Para => para([{ text, bold: true, size: 26, color: ACCENT }], { after: 4 });
const heading = (text: string): Para => para([{ text, bold: true, size: 14, color: ACCENT }], { before: 14, after: 4 });
const bullet = (text: string | Run[]): Para => para(text, { bullet: true, after: 2 });
const blank = (): Para => para("");

function toBlock(p: Para) {
    const inlines = p.runs
        .filter((r) => r.text.length > 0)
        .map((r) => ({
            text: r.text,
            characterFormat: {
                bold: r.bold || undefined,
                italic: r.italic || undefined,
                fontSize: r.size,
                fontColor: r.color
            }
        }));
    if (p.bullet) inlines.unshift({ text: "•  ", characterFormat: { bold: undefined, italic: undefined, fontSize: undefined, fontColor: ACCENT } });
    return {
        paragraphFormat: {
            styleName: "Normal",
            textAlignment: p.align,
            beforeSpacing: p.before,
            afterSpacing: p.after,
            leftIndent: p.bullet ? 18 : undefined,
            firstLineIndent: p.bullet ? -12 : undefined
        },
        characterFormat: {},
        inlines
    };
}

function sfdt(paras: Para[]): string {
    return JSON.stringify({
        sections: [
            {
                sectionFormat: {
                    pageWidth: 612,
                    pageHeight: 792,
                    leftMargin: 72,
                    rightMargin: 72,
                    topMargin: 72,
                    bottomMargin: 72,
                    headerDistance: 36,
                    footerDistance: 36
                },
                blocks: paras.map(toBlock),
                headersFooters: {}
            }
        ],
        characterFormat: { fontFamily: "Calibri", fontSize: 11 },
        paragraphFormat: { afterSpacing: 8, lineSpacing: 1.08, lineSpacingType: "Multiple" }
    });
}

function today(): string {
    return new Date().toLocaleDateString(undefined, { year: "numeric", month: "long", day: "numeric" });
}

interface DocTemplate extends HomeTemplate {
    fileName: string;
    build: () => string;
}

export const DOC_TEMPLATES: DocTemplate[] = [
    {
        id: "letter",
        name: "Letter",
        description: "Formal business letter",
        fileName: "Letter.docx",
        preview: { accent: ACCENT, blocks: ["heading", "short", "short", "gap", "short", "gap", "text", "text", "text", "gap", "text", "text", "short"] },
        build: () => sfdt([
            para([{ text: "Your Name", bold: true, size: 14, color: ACCENT }], { after: 0 }),
            para("Street address", { after: 0 }),
            para("City, Postcode", { after: 0 }),
            para("you@example.com · +00 0000 000000"),
            blank(),
            para(today()),
            blank(),
            para("Recipient Name", { after: 0 }),
            para("Title", { after: 0 }),
            para("Company", { after: 0 }),
            para("Street address, City, Postcode"),
            blank(),
            para("Dear Recipient Name,"),
            para("I am writing to … State the purpose of your letter in the opening paragraph so the reader knows at once why you are writing.", { align: "Justify" }),
            para("Use the next paragraph or two for the details: background, what you are asking for, and anything the reader needs in order to act on it.", { align: "Justify" }),
            para("Close by saying what happens next, and thank the reader for their time.", { align: "Justify" }),
            blank(),
            para("Sincerely,"),
            blank(),
            blank(),
            para([{ text: "Your Name", bold: true }])
        ])
    },
    {
        id: "meeting-notes",
        name: "Meeting notes",
        description: "Agenda, decisions, actions",
        fileName: "Meeting notes.docx",
        preview: { accent: "#2E75B6", blocks: ["title", "short", "gap", "heading", "bullet", "bullet", "heading", "bullet", "bullet", "heading", "text", "text"] },
        build: () => sfdt([
            title("Meeting notes"),
            para([{ text: `${today()}  ·  10:00–11:00  ·  Location / call link`, color: MUTED }]),
            heading("Attendees"),
            bullet("Name — role"),
            bullet("Name — role"),
            heading("Agenda"),
            bullet("1.  Topic"),
            bullet("2.  Topic"),
            bullet("3.  Topic"),
            heading("Discussion"),
            para("Key points raised, options considered and open questions."),
            heading("Decisions"),
            bullet("Decision and who made it"),
            heading("Action items"),
            bullet([{ text: "☐  Task", bold: true }, { text: " — owner — due date" }]),
            bullet([{ text: "☐  Task", bold: true }, { text: " — owner — due date" }]),
            heading("Next meeting"),
            para("Date, time and what to prepare.")
        ])
    },
    {
        id: "report",
        name: "Report",
        description: "Summary, findings, next steps",
        fileName: "Report.docx",
        preview: { accent: "#833C0B", blocks: ["title", "subtitle", "gap", "heading", "text", "text", "short", "heading", "text", "text", "heading", "bullet", "bullet"] },
        build: () => sfdt([
            title("Project report"),
            para([{ text: `Prepared by Your Name  ·  ${today()}`, italic: true, color: MUTED }], { after: 12 }),
            heading("Executive summary"),
            para("Two or three sentences a busy reader can stop after: what was done, what was found, and what you recommend.", { align: "Justify" }),
            heading("Background"),
            para("Why this work was needed, its scope, and anything the reader should know first.", { align: "Justify" }),
            heading("Findings"),
            bullet("Finding one, with the evidence behind it"),
            bullet("Finding two"),
            bullet("Finding three"),
            heading("Recommendations"),
            bullet("What should happen, and who should do it"),
            bullet("Estimated cost / effort and expected benefit"),
            heading("Next steps"),
            para("Milestones, owners and dates.")
        ])
    },
    {
        id: "resume",
        name: "Resume",
        description: "Clean one-page CV",
        fileName: "Resume.docx",
        preview: { accent: "#375623", blocks: ["title", "short", "gap", "heading", "text", "text", "heading", "short", "bullet", "bullet", "heading", "text"] },
        build: () => sfdt([
            para([{ text: "Your Name", bold: true, size: 28, color: "#375623" }], { after: 0 }),
            para([{ text: "City  ·  you@example.com  ·  +00 0000 000000  ·  linkedin.com/in/you", color: MUTED }], { after: 12 }),
            heading("Profile"),
            para("One or two lines on who you are, what you're good at and the role you're looking for."),
            heading("Experience"),
            para([{ text: "Job title", bold: true }, { text: "  ·  Company  ·  2022 – present", color: MUTED }], { after: 2 }),
            bullet("Achievement with a measurable result"),
            bullet("Responsibility or project you led"),
            para([{ text: "Job title", bold: true }, { text: "  ·  Company  ·  2019 – 2022", color: MUTED }], { before: 6, after: 2 }),
            bullet("Achievement with a measurable result"),
            heading("Education"),
            para([{ text: "Degree, subject", bold: true }, { text: "  ·  Institution  ·  Year", color: MUTED }]),
            heading("Skills"),
            para("Skill · Skill · Skill · Language (level)")
        ])
    }
];

/** SFDT + suggested file name for a template id, or null for an unknown id. */
export function buildDocTemplate(id: string): { sfdt: string; fileName: string } | null {
    const template = DOC_TEMPLATES.find((t) => t.id === id);
    return template ? { sfdt: template.build(), fileName: template.fileName } : null;
}
